import React, { useState, useEffect, useRef } from 'react';

import { ReactComponent as CalculateIcon } from '@assets/icons/calculator.svg';

import CommonImage from '@components/CommonImage';

import { getInputValue, formatNumericValue, parseInputValue, getPartTitle } from '../utils';

import DisplayCost from './DisplayCost';
import ConsumptionModal from './ConsumptionModal';

const FabricTable = ({
	data = [],
	fabricWiseMeasurements,
	cadConsumptions,
	totalCost = 0,
	onChange,
	onMeasurementsChange,
	isEditable = true,
	validationErrors = {},
	onReCost,
	selectedMeasurementSize,
	measurementSizeRange,
	selectedConsumptionType,
	onConsumptionTypeChange
}) => {
	const [fabrics, setFabrics] = useState([]);
	const isInitialMount = useRef(true);
	const prevDataRef = useRef(null);
	const [open, setOpen] = useState(false);

	useEffect(() => {
		const dataString = JSON.stringify(data);
		const prevDataString = JSON.stringify(prevDataRef.current);

		if (dataString === prevDataString) {
			return;
		}

		if (data && data.length > 0) {
			const newFabrics = data.map((item, index) => {
				const formattedItem = { ...item, id: item.id || `fabric-${index}` };

				if (item.aopPriceDto) {
					formattedItem.aopCost = item.aopPriceDto.fabric_aop_price;
					formattedItem.aopType = item.aopPriceDto.aop_type || '';
					formattedItem.aopColor = item.aopPriceDto.colors || '';
					formattedItem.composition = item.aopPriceDto.composition || '';
					formattedItem.totalFabricComposition = item.aopPriceDto.total_fabric_composition || '';
					formattedItem.aopPriceDto = { ...item.aopPriceDto };
				} else {
					formattedItem.aopCost = formattedItem.aopCost || '';
					formattedItem.aopType = '';
					formattedItem.aopColor = '';
					formattedItem.composition = '';
					formattedItem.totalFabricComposition = '';
					formattedItem.aopPriceDto = {};
				}

				const numericFields = [
					'fabricCostWithoutElasticity',
					'knittingPrice',
					'fabricDyeingPrice',
					'heatSetPrice',
					'peacePrice',
					'finishingPrice',
					'brushPrice',
					'bitCutWashPrice',
					'yarnDyeingPrice',
					'specialFinishPrice',
					'elasticityCost',
					'aopCost',
					'consumption',
					'processLossPercentage',
					'totalShellPrice',
					'totalPrice'
				];

				numericFields.forEach((field) => {
					if (
						formattedItem[field] !== undefined &&
						formattedItem[field] !== null &&
						formattedItem[field] !== ''
					) {
						formattedItem[field] = formatNumericValue(formattedItem[field]);
					}
				});

				return formattedItem;
			});
			setFabrics(newFabrics);
		} else if (isInitialMount.current || fabrics.length === 0) {
			setFabrics([
				{
					id: 'fabric-0',
					partName: 'Fabric 1',
					fabricCostWithoutElasticity: '',
					knittingPrice: '',
					fabricDyeingPrice: '',
					heatSetPrice: '',
					peacePrice: '',
					finishingPrice: '',
					brushPrice: '',
					bitCutWashPrice: '',
					yarnDyeingPrice: '',
					specialFinishPrice: '',
					elasticityPercentage: '',
					elasticityCost: '',
					aopCost: '',
					aopType: '',
					aopColor: '',
					composition: '',
					totalFabricComposition: '',
					consumption: '',
					processLossPercentage: '',
					aopPriceDto: {},
					yarnCount: '',
					totalShellPrice: '',
					totalPrice: ''
				}
			]);
		}

		prevDataRef.current = data;
		if (isInitialMount.current) {
			isInitialMount.current = false;
		}
	}, [data]);

	const transformToApiFormat = (updatedFabrics) => {
		return updatedFabrics.map((fabric) => {
			const { aopCost, aopType, aopColor, composition, totalFabricComposition, ...rest } = fabric;
			const transformedFabric = { ...rest };

			if (fabric.aopPriceDto || aopCost !== undefined || aopType || aopColor) {
				transformedFabric.aopPriceDto = {
					...(fabric.aopPriceDto || {}),
					fabric_aop_price:
						aopCost !== undefined && aopCost !== ''
							? formatNumericValue(aopCost)
							: fabric.aopPriceDto?.fabric_aop_price || '',
					aop_type: aopType !== undefined ? aopType : fabric.aopPriceDto?.aop_type || '',
					colors: aopColor !== undefined ? aopColor : fabric.aopPriceDto?.colors || '',
					composition:
						composition !== undefined ? composition : fabric.aopPriceDto?.composition || '',
					total_fabric_composition:
						totalFabricComposition !== undefined
							? totalFabricComposition
							: fabric.aopPriceDto?.total_fabric_composition || ''
				};
			}

			return transformedFabric;
		});
	};

	const notifyChange = (updatedFabrics) => {
		if (onChange && !isInitialMount.current) {
			const transformedFabrics = transformToApiFormat(updatedFabrics);
			onChange(transformedFabrics);
		}
	};

	const handleAddFabric = () => {
		const newFabric = {
			id: `fabric-${Date.now()}`,
			partName: `Fabric ${fabrics.length + 1}`,
			fabricCostWithoutElasticity: '',
			knittingPrice: '',
			fabricDyeingPrice: '',
			heatSetPrice: '',
			peacePrice: '',
			finishingPrice: '',
			brushPrice: '',
			bitCutWashPrice: '',
			yarnDyeingPrice: '',
			specialFinishPrice: '',
			elasticityPercentage: '',
			elasticityCost: '',
			aopCost: '',
			aopType: '',
			aopColor: '',
			composition: '',
			totalFabricComposition: '',
			consumption: '',
			processLossPercentage: '',
			aopPriceDto: {},
			yarnCount: '',
			totalShellPrice: '',
			totalPrice: ''
		};
		const updatedFabrics = [...fabrics, newFabric];
		setFabrics(updatedFabrics);
		notifyChange(updatedFabrics);
	};

	const handleRemoveFabric = (id) => {
		if (fabrics.length > 1) {
			const updatedFabrics = fabrics.filter((f) => f.id !== id);
			setFabrics(updatedFabrics);
			notifyChange(updatedFabrics);
		}
	};

	const handleInputChange = (fabricId, field, value) => {
		const textFields = [
			'fabricName',
			'elasticityPercentage',
			'aopType',
			'aopColor',
			'composition',
			'totalFabricComposition',
			'yarnCount'
		];
		const isTextField = textFields.includes(field);
		const isLLMCallRequired = ['fabricName', 'yarnCount'].includes(field);

		const updatedValue = parseInputValue(value, !isTextField);
		const updatedFabrics = fabrics.map((f) =>
			f.id === fabricId
				? {
						...f,
						[field]: updatedValue,
						isFabricChanged: isLLMCallRequired
					}
				: f
		);
		setFabrics(updatedFabrics);
		notifyChange(updatedFabrics);
	};

	const toggleConsumptionTypeChange = (type) => {
		if (type === 'CAD_CONSUMPTION') {
			const updatedFabrics = fabrics.map((fabric, idx) => {
				fabric.consumption = cadConsumptions[idx]?.consumption || '';
				return fabric;
			});
			setFabrics(updatedFabrics);
			notifyChange(updatedFabrics);
			onConsumptionTypeChange(type);
		} else if (type === 'MEASUREMENT_CHART') {
			const updatedFabrics = fabrics.map((fabric) => {
				if (fabric.isMainFabric === true) {
					fabric.consumption =
						fabricWiseMeasurements['mainFabric']?.reduce(
							(acc, item) => acc + (item.final_fabric_consumption_kg || 0),
							0
						) || '';
				} else {
					fabric.consumption =
						fabricWiseMeasurements[fabric.partName]?.reduce(
							(acc, item) => acc + (item.final_fabric_consumption_kg || 0),
							0
						) || '';
				}
				return fabric;
			});
			setFabrics(updatedFabrics);
			notifyChange(updatedFabrics);
			onConsumptionTypeChange(type);
		}
	};

	return (
		<>
			<div className="single-table costing-table-content">
				<div className="costing-table scroll-x-label">
					<table className="table no-hover">
						<thead>
							<tr>
								<th>Fabric Cost Metrics</th>
								{fabrics.map((fabric, index) => (
									<th key={fabric.id}>
										<div className="d-flex align-items-center justify-content-between">
											<p>{getPartTitle(`part${index + 1}`)}</p>
											{fabrics.length > 1 && isEditable && (
												<CommonImage
													src="/icons/chip-close.svg"
													alt="remove"
													width={20}
													height={20}
													className="cursor-pointer"
													onClick={() => handleRemoveFabric(fabric.id)}
												/>
											)}
										</div>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Fabrication</p>
								</td>
								{fabrics.map((fabric, index) => {
									const errorKey = `fabric-${index}-fabricName`;
									const hasError = validationErrors[errorKey];
									return (
										<td key={fabric.id}>
											<input
												type="text"
												value={fabric.fabricName || ''}
												onChange={(e) =>
													handleInputChange(fabric.id, 'fabricName', e.target.value)
												}
												readOnly={!isEditable}
												className={hasError ? 'common-border-error' : ''}
											/>
										</td>
									);
								})}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Yarn Count</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="text"
											value={fabric.yarnCount || ''}
											onChange={(e) =>
												handleInputChange(fabric.id, 'yarnCount', e.target.value)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Yarn Price/kg ($)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.fabricCostWithoutElasticity)}
											onChange={(e) =>
												handleInputChange(
													fabric.id,
													'fabricCostWithoutElasticity',
													e.target.value
												)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Elastane (%)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="text"
											value={fabric?.elasticityPercentage || ''}
											onChange={(e) =>
												handleInputChange(
													fabric.id,
													'elasticityPercentage',
													e.target.value
												)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02"> Elastane Price/kg ($)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.elasticityCost)}
											onChange={(e) =>
												handleInputChange(fabric.id, 'elasticityCost', e.target.value)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Knitting Cost/kg ($)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.knittingPrice)}
											onChange={(e) =>
												handleInputChange(fabric.id, 'knittingPrice', e.target.value)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Yarn Dyeing Cost/kg ($)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.yarnDyeingPrice)}
											onChange={(e) =>
												handleInputChange(
													fabric.id,
													'yarnDyeingPrice',
													e.target.value
												)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Fabric Dyeing Cost/kg ($)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.fabricDyeingPrice)}
											onChange={(e) =>
												handleInputChange(
													fabric.id,
													'fabricDyeingPrice',
													e.target.value
												)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Finishing Cost/kg ($)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.finishingPrice)}
											onChange={(e) =>
												handleInputChange(fabric.id, 'finishingPrice', e.target.value)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Bit Cut Wash Price ($)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.bitCutWashPrice)}
											onChange={(e) =>
												handleInputChange(
													fabric.id,
													'bitCutWashPrice',
													e.target.value
												)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Brush Price ($)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.brushPrice)}
											onChange={(e) =>
												handleInputChange(fabric.id, 'brushPrice', e.target.value)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Heat Setting/kg ($)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.heatSetPrice)}
											onChange={(e) =>
												handleInputChange(fabric.id, 'heatSetPrice', e.target.value)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Peach Cost/kg ($)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.peacePrice)}
											onChange={(e) =>
												handleInputChange(fabric.id, 'peacePrice', e.target.value)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Special Finish/kg ($)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.specialFinishPrice)}
											onChange={(e) =>
												handleInputChange(
													fabric.id,
													'specialFinishPrice',
													e.target.value
												)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>

							<tr>
								<td>
									<p className="regular-12 gray_dark_02">AOP Type</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="text"
											value={fabric.aopType || ''}
											onChange={(e) =>
												handleInputChange(fabric.id, 'aopType', e.target.value)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">AOP Color</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="text"
											value={fabric.aopColor || ''}
											onChange={(e) =>
												handleInputChange(fabric.id, 'aopColor', e.target.value)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td>
									<p className="regular-12 gray_dark_02">AOP Cost/kg ($)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.aopCost)}
											onChange={(e) =>
												handleInputChange(fabric.id, 'aopCost', e.target.value)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>

							<tr>
								<td>
									<p className="regular-12 gray_dark_02">Process Loss (%)</p>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.processLossPercentage)}
											onChange={(e) =>
												handleInputChange(
													fabric.id,
													'processLossPercentage',
													e.target.value
												)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td className="cursor-pointer">
									<div className="d-flex align-items-center justify-content-start">
										<p className="regular-12 gray_dark_02"> Fabric Price/kg ($)</p>
									</div>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.totalPrice)}
											onChange={(e) =>
												handleInputChange(fabric.id, 'totalPrice', e.target.value)
											}
											readOnly={true}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td className="cursor-pointer" onClick={() => setOpen(true)}>
									<div className="d-flex align-items-center justify-content-start">
										<p className="regular-12 gray_dark_02">Consumption (Kg)</p>
										<CalculateIcon width={16} height={24} className="ml-1" />
									</div>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.consumption)}
											onChange={(e) =>
												handleInputChange(fabric.id, 'consumption', e.target.value)
											}
											readOnly={!isEditable}
										/>
									</td>
								))}
							</tr>
							<tr>
								<td className="cursor-pointer">
									<div className="d-flex align-items-center justify-content-start">
										<p className="regular-12 gray_dark_02">Fabric cost per shell</p>
									</div>
								</td>
								{fabrics.map((fabric) => (
									<td key={fabric.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(fabric.totalShellPrice)}
											onChange={(e) =>
												handleInputChange(
													fabric.id,
													'totalShellPrice',
													e.target.value
												)
											}
											readOnly={true}
										/>
									</td>
								))}
							</tr>
							<tr className="cost-total-row">
								<td>
									<p className="semibold-14 gray_dark_01">Total Fabric Cost</p>
								</td>
								<td colSpan={fabrics.length}>
									<p className="semibold-14 gray_dark_01">
										<DisplayCost totalCost={totalCost} />
									</p>
								</td>
							</tr>
						</tbody>
					</table>

					{isEditable && (
						<div className="add-more-button w-100">
							<button className="button gray-outline size32 w-100" onClick={handleAddFabric}>
								<CommonImage src="/icons/plus-20.svg" alt="add" width={20} height={20} />
								<span className="ml-1 regular-14 gray_dark_01">Add Fabric</span>
							</button>
						</div>
					)}
				</div>
			</div>
			{open && (
				<ConsumptionModal
					fabricWiseMeasurements={fabricWiseMeasurements}
					cadConsumptions={cadConsumptions}
					open={open}
					onClose={() => setOpen(false)}
					isEditable={isEditable}
					onChange={onMeasurementsChange}
					onReCostMeasurement={onReCost}
					measurementSizeRange={measurementSizeRange}
					selectedMeasurementSize={selectedMeasurementSize}
					selectedConsumptionType={selectedConsumptionType}
					toggleConsumptionTypeChange={toggleConsumptionTypeChange}
				/>
			)}
		</>
	);
};
export default FabricTable;
